import React from 'react';
import { Play, Pencil, Trash2 } from 'lucide-react';
import { Td, Tr } from './ui/Table';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { StatusBadge } from './StatusBadge';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TaskAPI } from '../lib/api';
import { useToast } from './ui/Toast';
import { formatRelative } from '../lib/format';
import { useNavigate } from '@tanstack/react-router';
import cronstrue from 'cronstrue';

interface Task {
  id: number;
  name: string;
  ownerProject?: string;
  schedule: string;
  type: string;
  lastRunAt?: string;
  lastRunStatus?: string;
  enabled: boolean;
}

interface TaskRowProps {
  task: Task;
}

export function TaskRow({ task }: TaskRowProps) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const toggleMut = useMutation({
    mutationFn: () => task.enabled ? TaskAPI.disable(task.id) : TaskAPI.enable(task.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const runMut = useMutation({
    mutationFn: () => TaskAPI.run(task.id),
    onSuccess: () => toast(`Задачу "${task.name}" запущено`, 'success'),
    onError: (e: Error) => toast(e.message, 'error'),
  });

  let humanSchedule = task.schedule;
  try {
    humanSchedule = cronstrue.toString(task.schedule);
  } catch {}

  return (
    <Tr onClick={() => navigate({ to: '/tasks/$taskId', params: { taskId: String(task.id) } })}>
      <Td className="font-medium">{task.name}</Td>
      <Td className="text-tg-text-secondary">{task.ownerProject || '—'}</Td>
      <Td>
        <span className="font-mono text-xs text-tg-accent">{task.schedule}</span>
        <div className="text-xs text-tg-text-secondary">{humanSchedule}</div>
      </Td>
      <Td><Badge variant="muted">{task.type}</Badge></Td>
      <Td className="text-tg-text-secondary text-xs">
        {task.lastRunAt ? formatRelative(task.lastRunAt) : '—'}
      </Td>
      <Td>
        {task.lastRunStatus ? <StatusBadge status={task.lastRunStatus} /> : '—'}
      </Td>
      <Td>
        <button
          onClick={(e) => { e.stopPropagation(); toggleMut.mutate(); }}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${task.enabled ? 'bg-tg-success' : 'bg-tg-border'}`}
          style={{ background: task.enabled ? 'var(--tg-success)' : 'var(--tg-border)' }}
        >
          <span
            className={`inline-block h-3 w-3 rounded-full bg-white transition-transform ${task.enabled ? 'translate-x-5' : 'translate-x-1'}`}
          />
        </button>
      </Td>
      <Td>
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            loading={runMut.isPending}
            onClick={() => runMut.mutate()}
            title="Запустити"
          >
            <Play size={12} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate({ to: '/tasks/$taskId', params: { taskId: String(task.id) } })}
            title="Редагувати"
          >
            <Pencil size={12} />
          </Button>
        </div>
      </Td>
    </Tr>
  );
}
